import React from 'react';
import { FormikConfig, Formik } from 'formik';

import { Link } from 'react-router-dom';
import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import { makeValidator } from 'mandle';
import { pipe } from 'fp-ts/lib/function';
import {
  FullMatchFragment,
  MatchPlayerFragment,
  useCreateMatchEventMutation,
} from '../../../generated/graphql';
import { useAlert } from '../../../contexts/AlertContext';
import { useAnalytics } from '../../../contexts/AnalyticsContext';
import { formatErrors } from '../../../utils/formatErrors';
import { Heading } from '../../../components/Heading';
import { ListSelector } from '../../../components/ListSelector';
import { InputError } from '../../../components/InputError';
import { SubmitButton } from '../../../components/SubmitButton';
import { Button } from '../../../components/Button';
import { required2 } from '../../../validators';
import { CardIcon } from '../components/CardIcon';
import { paths } from '../../../paths';
import { PlayerNameWithAvatar } from '../components/PlayerNameWithAvatar';
import { CardEventType, CARD_EVENTS, getCardName } from './utils';

interface Fields {
  playerId: string;
  cardType: CardEventType;
}

interface Props {
  match: FullMatchFragment;
  onCancel: () => void;
}

export const PickCardRecipient: React.FC<Props> = ({ match, onCancel }) => {
  const [createCardRecipientResponse, createCardRecipient] =
    useCreateMatchEventMutation();

  const alert = useAlert();
  const analytics = useAnalytics();

  if (match.team.isCaptain && A.isEmpty(match.players)) {
    return (
      <p>
        <Link to={paths.match.settings.squad(match.id)}>
          Set a team for this match
        </Link>{' '}
        to set card recipients.
      </p>
    );
  }

  /**
   * If a squad has been selected for this match, only show those players
   * on the vote form. Otherwise just show the whole squad
   */
  const players = A.isEmpty(match.players) ? match.team.players : match.players;

  const submitCardedPlayer: FormikConfig<Fields>['onSubmit'] = (
    values,
    actions,
  ) => {
    const formEvent = { form: 'pick_carded_player' };
    analytics.track('Form Submitted', formEvent);

    createCardRecipient({
      input: {
        playerId: values.playerId,
        matchId: match.id,
        event: values.cardType,
      },
    }).then((response) => {
      pipe(
        response.data?.createMatchEvent?.validation,
        O.fromNullable,
        O.map(formatErrors),
        O.fold(() => {
          analytics.track('Data Submit Succeeded', formEvent);
          alert('Card confirmed');
          onCancel();
        }, actions.setErrors),
      );
    });
  };

  return (
    <div className="space-y-4">
      <Heading>Add a carded player</Heading>
      <PickCardedPlayersForm
        initialValues={{
          cardType: 'YELLOW_CARD',
          playerId: '',
        }}
        loading={createCardRecipientResponse.fetching}
        players={players}
        onSubmit={submitCardedPlayer}
        onCancel={onCancel}
      />
    </div>
  );
};

interface PickCardedPlayersFormProps {
  initialValues: Fields;
  loading: boolean;
  players: MatchPlayerFragment[];
  onSubmit: FormikConfig<Fields>['onSubmit'];
  onCancel: () => void;
}

const validate = makeValidator<Fields>({
  playerId: [required2('Select a player received a card')],
  cardType: [required2('Select a card type')],
});

export const PickCardedPlayersForm: React.FC<PickCardedPlayersFormProps> = (
  props,
) => {
  return (
    <Formik
      initialValues={props.initialValues}
      onSubmit={props.onSubmit}
      validate={validate}
    >
      {({ handleChange, values, handleSubmit, errors }) => {
        return (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <ListSelector
                label="Player"
                onChange={handleChange('playerId')}
                value={values.playerId}
                options={props.players.map((player) => ({
                  name: <PlayerNameWithAvatar player={player} />,
                  value: player.id,
                  searchValue: player.name,
                }))}
                showFilter={props.players.length > 15}
              />

              {!!errors && !!errors.playerId && (
                <InputError>{errors.playerId}</InputError>
              )}
            </div>
            <div>
              <ListSelector
                label="Card type"
                onChange={handleChange('cardType')}
                value={values.cardType}
                options={CARD_EVENTS.map((card) => ({
                  name: (
                    <div className="flex items-center">
                      <CardIcon color={card} className="mr-1" />
                      {getCardName(card)}
                    </div>
                  ),
                  value: card,
                }))}
              />
              {!!errors && !!errors.cardType && (
                <InputError>{errors.cardType}</InputError>
              )}
            </div>

            <SubmitButton loading={props.loading}>Save card</SubmitButton>
            <Button onClick={props.onCancel} kind="plain" icon="x">
              Cancel
            </Button>
          </form>
        );
      }}
    </Formik>
  );
};
